import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogActions, Box, Typography, Button, makeStyles, Divider, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  headerWrap: {
    position: "relative",
    background: "linear-gradient(135deg, #1b6197 0%, #155A8A 100%)",
    padding: theme.spacing(3),
    color: "#fff",
    overflow: "hidden",
  },
  accent: {
    position: "absolute",
    right: -40,
    top: -40,
    width: 160,
    height: 160,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
  },
  title: {
    fontWeight: 800,
    fontSize: "1.6rem",
    letterSpacing: 0.2,
  },
  subtitle: {
    opacity: 0.95,
    marginTop: 6,
  },
  shield: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
  },
  content: {
    background: "#fff",
    color: "#000",
    paddingTop: theme.spacing(2),
  },
  listDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
    display: "inline-block",
    marginRight: 8,
  },
  noteBox: {
    border: "1px solid #e6eef6",
    borderRadius: 12,
    background: "#f7fbff",
    padding: theme.spacing(2.25),
  },
  primaryBtn: {
    backgroundColor: "#b3daff !important",
    color: "#000 !important",
    borderRadius: 12,
    padding: "12px 26px",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#1b6197 !important", color: "#fff !important" },
  },
  ghostBtn: {
    border: "1px solid #b3daff !important",
    color: "#1b6197 !important",
    borderRadius: 12,
    padding: "12px 26px",
    textTransform: "none",
  },
  paper: {
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
  },
  fullscreenModalPaper: {
    borderRadius: 0,
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    margin: 0,
  },
  iframeWrap: {
    position: "relative",
    width: "100%",
    height: "calc(100vh - 56px)",
    background: "#000",
  },
  iframe: {
    border: 0,
    width: "100%",
    height: "100%",
  },
  topBar: {
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px",
    background: "#0f172a",
    color: "#fff",
  },
}));

export default function KycPrompt({ open, onLater, onStart }) {
  const classes = useStyles();
  const [isSumsubOpen, setIsSumsubOpen] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const containerRef = useRef(null);
  const sdkLaunchedRef = useRef(false);

  const handleStart = async () => {
    try {
      // fetch short‑lived WebSDK access token from backend
      const token = await fetchAccessToken();
      if (token) {
        setAccessToken(token);
        setIsSumsubOpen(true);
      }
    } catch (e) {
      setIsSumsubOpen(true); // fallback to iframe if token fetch fails
    }
  };

  async function fetchAccessToken() {
    try {
      const token = window.localStorage.getItem("token");
      const res = await fetch(`http://localhost:3001/api/v1/compliance/sumsub/access-token?levelName=Vintage-KYC`, {
        method: 'GET',
        headers: {
          token,
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      const json = await res.json();
      if (json?.result?.token) return json.result.token;
      return null;
    } catch (e) { return null; }
  }

  // Load Sumsub WebSDK script once
  function loadWebSdkScript() {
    return new Promise((resolve) => {
      if (window.snsWebSdk) return resolve(true);
      const existing = document.querySelector('#sumsub-websdk-script');
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        return;
      }
      const s = document.createElement('script');
      s.id = 'sumsub-websdk-script';
      s.src = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js';
      s.async = true;
      s.onload = () => resolve(true);
      document.body.appendChild(s);
    });
  }

  // Launch SDK inside modal when token is ready
  useEffect(() => {
    (async () => {
      if (!isSumsubOpen) return;
      if (!accessToken) return;
      if (sdkLaunchedRef.current) return;
      await loadWebSdkScript();
      if (!window.snsWebSdk) return;

      try {
        const getNewAccessToken = async () => {
          const t = await fetchAccessToken();
          return t || '';
        };

        const instance = window.snsWebSdk
          .init(accessToken, () => getNewAccessToken())
          .withConf({ lang: 'en' })
          .withOptions({ addViewportTag: false, adaptIframeHeight: true })
          .on('idCheck.onError', (e) => { /* silent */ })
          .onMessage((type, payload) => { /* optional logging */ })
          .build();
        instance.launch('#sumsub-websdk-container');
        sdkLaunchedRef.current = true;
      } catch (e) {
        // If SDK fails for any reason, fallback is already rendered.
      }
    })();
  }, [isSumsubOpen, accessToken]);

  const handleCloseSumsub = () => {
    setIsSumsubOpen(false);
    // Optionally trigger a status refresh in parent after close
    // e.g., parent can refetch profile to update KYC status
  };
  return (
    <>
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ className: classes.paper }}>
      <Box className={classes.headerWrap}>
        <Box className={classes.accent} />
        <Box display="flex" alignItems="center" gridGap={12}>
          <Box className={classes.shield}>🛡️</Box>
          <Box>
            <Typography className={classes.title}>Complete Your KYC for NFT Trading</Typography>
            <Typography variant="caption" className={classes.subtitle}>Secure your account to mint, buy, sell and withdraw without limits</Typography>
          </Box>
        </Box>
      </Box>
      <DialogContent className={classes.content}>
        <Box>
          <Typography variant="h6" style={{ color: "#0f172a", fontWeight: 800 }} gutterBottom>
            Why KYC on an NFT Marketplace?
          </Typography>
          <Typography variant="body2" style={{ color: "#334155" }}>
            KYC helps prevent fraud, protects creators and collectors, enables fiat on/off‑ramps, and keeps our platform compliant with global AML/CTF regulations. Completing verification unlocks higher limits and faster withdrawals.
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#0f172a" }}>What you'll need</Typography>
          <Divider style={{ margin: "8px 0 12px", background: "#e6eef6" }} />
          <Box mt={1}>
            <Typography variant="body2" style={{ color: "#111827" }}><span className={classes.listDot} />Government‑issued ID (passport, national ID, or driver’s license)</Typography>
            <Typography variant="body2" style={{ color: "#111827" }}><span className={classes.listDot} />Proof of address (utility bill or bank statement ≤ 3 months)</Typography>
            <Typography variant="body2" style={{ color: "#111827" }}><span className={classes.listDot} />Selfie / liveness check to match your ID</Typography>
            <Typography variant="body2" style={{ color: "#111827" }}><span className={classes.listDot} />Optional: Proof of wallet ownership (sign a message in your wallet)</Typography>
          </Box>
        </Box>

        <Box mt={3} className={classes.noteBox}>
          <Typography variant="body2" style={{ color: "#0f172a" }}>
            🔒 Your data is encrypted at rest and in transit. We use bank‑level security and only share with trusted verification providers to meet international privacy and compliance standards.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: 20, backgroundColor: "#fff" }}>
        <Button onClick={onLater} className={classes.primaryBtn} variant="outlined">Complete Later</Button>
        <Button onClick={handleStart} className={classes.primaryBtn} variant="contained">Start Verification</Button>
      </DialogActions>
    </Dialog>

    {/* Fullscreen embedded Sumsub modal (iframe fallback) */}
    <Dialog
      open={isSumsubOpen}
      fullScreen
      PaperProps={{ className: classes.fullscreenModalPaper }}
    >
      <Box className={classes.topBar}>
        <Typography style={{ fontWeight: 700 }}>KYC Verification</Typography>
        <IconButton onClick={handleCloseSumsub} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.iframeWrap} ref={containerRef}>
        {!accessToken ? (
          <iframe
            title="Sumsub WebSDK Fallback"
            className={classes.iframe}
            src={`https://in.sumsub.com/websdk/p/sbx_uni_c15Gy368t4vqBxff`}
            allow="camera;microphone;fullscreen;clipboard-read;clipboard-write"
          />
        ) : (
          <div id="sumsub-websdk-container" className={classes.iframe} />
        )}
      </Box>
    </Dialog>
    </>
  );
}


